import { sanitizeErrorMessage, captureStackTrace } from '@exodus/errors';
import { safeString } from '@exodus/safe-string';
import { isNumberUnit } from '@exodus/currency';
import lodash from 'lodash';
import { classifyExchangeError } from './error-classifier.js';
const { cloneDeepWith } = lodash;
const isExchangeError = (error) => error instanceof Error && 'isExchange' in error && error.isExchange === true;
const MAX_DEPTH = 2;
const toJSON = (error, depth = 0) => {
    const serializedDetails = error.details
        ? cloneDeepWith(error.details, (val) => {
            if (val && isNumberUnit(val))
                return val.toDefaultString({ unit: true });
            if (typeof val === 'string')
                return sanitizeErrorMessage(val);
        })
        : null;
    const serializedCause = isExchangeError(error.cause) && depth < MAX_DEPTH ? toJSON(error.cause, depth + 1) : undefined;
    return {
        name: error.name,
        cause: serializedCause,
        message: sanitizeErrorMessage(error.message),
        ...(serializedDetails ? { details: serializedDetails } : {}),
        ...(process.env.NODE_ENV === 'test' ? {} : { stack: sanitizeErrorMessage(error.stack ?? '') }),
    };
};
export class ExchangeError extends Error {
    name = safeString `Exchange Error`;
    hint;
    isExchange = true;
    orderId;
    details;
    constructor({ message, details, orderId }, opts) {
        super(message, opts);
        this.hint = classifyExchangeError(message);
        this.details = details;
        this.orderId = orderId;
        if (opts?.cause instanceof Error) {
            captureStackTrace(opts.cause);
        }
    }
    toJSON = () => toJSON(this);
}
export class ExchangeOrderError extends ExchangeError {
    name = safeString `Exchange Order Error`;
    orderId;
    constructor({ message, orderId, details }, opts) {
        super({ message, details }, opts);
        this.orderId = orderId;
    }
}
export class CreateOrderError extends ExchangeOrderError {
    name = safeString `Create Order Error`;
}
export class CreateDexOrderError extends ExchangeOrderError {
    name = safeString `Create Dex Order Error`;
}
export class GetProviderOrderError extends ExchangeOrderError {
    name = safeString `Get Provider Order Error`;
}
export class OrderValidationError extends ExchangeOrderError {
    name = safeString `Order Validation Error`;
    constructor({ expected, actual, order, providerOrder }, opts) {
        const details = {
            order,
            providerOrder,
        };
        const message = `expected ${expected}, got ${actual}`;
        super({ message, details, orderId: order?.id }, opts);
    }
}
export class SimulationError extends ExchangeOrderError {
    name = safeString `Simulation Error`;
}
export class JupiterSimulationError extends ExchangeOrderError {
    name = safeString `Jupiter Simulation Error`;
}
const serializeBalanceChanges = (balanceChanges) => balanceChanges?.map((change) => ({
    balance: change.balance.toDefaultString(),
    asset: change.asset,
}));
export class InvalidAmountError extends OrderValidationError {
    name = safeString `Invalid Amount Error`;
    constructor({ expected, actual, order, providerOrder, willSend, willReceive }, opts) {
        super({ expected, actual, order, providerOrder }, opts);
        if (willSend || willReceive) {
            this.details = {
                ...this.details,
                ...(willSend && { willSend: serializeBalanceChanges(willSend) }),
                ...(willReceive && { willReceive: serializeBalanceChanges(willReceive) }),
            };
        }
    }
}
export class InvalidFromTokenAddressError extends OrderValidationError {
    name = safeString `Invalid From Token Address Error`;
}
export class InvalidToTokenAddressError extends OrderValidationError {
    name = safeString `Invalid To Token Address Error`;
}
export class InvalidPayInAddressError extends ExchangeOrderError {
    name = safeString `Invalid Pay In Address Error`;
}
export class CreateTxError extends ExchangeOrderError {
    name = safeString `Create Tx Error`;
}
export class CreateDexTxError extends ExchangeOrderError {
    name = safeString `Create Tx Error`;
}
export class SendTxError extends ExchangeError {
    name = safeString `Send Tx Error`;
}
export class SignTxError extends ExchangeError {
    name = safeString `Sign Tx Error`;
}
export class GetAllowanceError extends ExchangeError {
    name = safeString `Get Allowance Error`;
    constructor({ message, assetId, approvalAddress }, opts) {
        const details = {
            assetId,
            approvalAddress,
        };
        super({ message, details }, opts);
    }
}
export class ApproveTokensError extends ExchangeError {
    name = safeString `Approve Tokens Error`;
    constructor({ message, assetId, approvalAddress, amount }, opts) {
        const details = {
            assetId,
            approvalAddress,
            amount: amount.toDefaultString(),
        };
        super({ message, details }, opts);
    }
}
export class ExchangeLimitsError extends ExchangeError {
    name = safeString `Exchange Limits Error`;
}
export class MissingPairError extends ExchangeError {
    name = safeString `Missing Pair Error`;
    constructor({ fromAsset, toAsset, pairId }) {
        const message = `Pair ${pairId} not found, ${fromAsset === undefined ? 'from asset is missing' : ``} ${toAsset === undefined ? 'to asset is missing' : ``}`;
        super({ message });
    }
}
export class MissingToAssetError extends ExchangeError {
    name = safeString `Missing To Asset Error`;
    orderId;
    constructor({ toAsset, orderId }) {
        const message = `To asset ${toAsset} is missing`;
        super({ message });
        this.orderId = orderId;
    }
}
export class MissingFromAssetError extends ExchangeError {
    name = safeString `Missing From Asset Error`;
    orderId;
    constructor({ fromAsset, orderId }) {
        const message = `From asset ${fromAsset} is missing`;
        super({ message });
        this.orderId = orderId;
    }
}
export class MissingFeeAssetError extends ExchangeError {
    name = safeString `Missing Fee Asset Error`;
    constructor({ feeAsset }) {
        const message = `Fee asset ${feeAsset} is missing`;
        super({ message });
    }
}
export class MissingPairPriceError extends ExchangeError {
    name = safeString `Missing Pair Price Error`;
    constructor({ pairId, fromAssetPrice, toAssetPrice }) {
        const message = `Pair price for ${pairId} is missing, ${fromAssetPrice === undefined ? 'from asset price is missing' : ``} ${toAssetPrice === undefined ? 'to asset price is missing' : ``}`;
        super({ message });
    }
}
export class ProviderQuoteError extends ExchangeError {
    name = safeString `Provider Quote Error`;
}
export class JupiterProviderError extends ExchangeError {
    name = safeString `Jupiter Provider Error`;
}
export class EVMSimulationError extends ExchangeError {
    name = safeString `EVM Simulation Error`;
}
export class LifiProviderError extends ExchangeError {
    name = safeString `Lifi Provider Error`;
}
export class TransactionDecoderError extends ExchangeError {
    name = safeString `Transaction Decoder Error`;
}
export class NoRateAvailableError extends ExchangeError {
    name = safeString `No Rate Available`;
    constructor({ amount, min, max }) {
        const message = `No rate available for amount ${amount}, min ${min}, max ${max}`;
        super({ message });
    }
}
export class NoQuoteAvailableError extends ExchangeError {
    name = safeString `No Quote Available`;
    constructor({ minValue, maxValue, inputAmount, swapMode, pairId, }) {
        const message = `No quote available for ${inputAmount} (${swapMode}). Min value ${minValue}, max value ${maxValue}, pairId ${pairId}`;
        super({ message });
    }
}
export class MissingProviderOrderError extends ExchangeError {
    name = safeString `Missing Provider Order Error`;
}
export class RangoProviderError extends ExchangeError {
    name = safeString `Rango Provider Error`;
}
export class AssetsServiceError extends ExchangeError {
    name = safeString `Assets Service Error`;
}
export class ProviderVerifiersError extends ExchangeError {
    name = safeString `Provider Verifiers Error`;
}
